import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ServicesSectionSkeleton from './ServicesSectionSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import LocalizedNavLink from '../../hooks/LocalizedNavLink';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './ServicesSection.scss';

const ServicesSection = ({ page }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const {
    data: servicesJson,
    loading: dataLoading,
    error: dataError,
    refetch
  } = useCachedFetch('/data/locales/services.json');

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const data = servicesJson?.pages?.[page];

    if (data) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, servicesJson, page]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const pageData = useMemo(() =>
    servicesJson?.pages?.[page] || null,
    [servicesJson, page]
  );

  const getLocalizedText = useCallback((textObj) => {
    if (!textObj) return '';
    if (typeof textObj === 'object') {
      return textObj[currentLang] || textObj.en || '';
    }
    return textObj;
  }, [currentLang]);

  const mainTranslations = useMemo(() => {
    if (!pageData) return {};

    return {
      accent: getLocalizedText(pageData.accent),
      mainTitle: getLocalizedText(pageData.mainTitle),
      subtitle: getLocalizedText(pageData.subtitle),
      buttonText: getLocalizedText(pageData.button?.text),
      buttonLink: pageData.button?.link || '/'
    };
  }, [pageData, getLocalizedText]);

  const localizedServices = useMemo(() => {
    if (!pageData?.servicesList) return [];

    return pageData.servicesList.map(service => ({
      ...service,
      localizedTitle: getLocalizedText(service.title),
      localizedDescription: getLocalizedText(service.description),
      link: service.link || '/'
    }));
  }, [pageData, getLocalizedText]);

  const hasValidData = useMemo(() =>
    !hasError && pageData && localizedServices.length > 0,
    [hasError, pageData, localizedServices.length]
  );

  if (isLoading) {
    return <ServicesSectionSkeleton />;
  }

  if (!hasValidData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  return (
    <div className="home__services__section" id="need__section">
      <div className="left__side__services__section">
        <div className="services__content">
          <AnimatedOnScroll animation="fade-up" delay={0}>
            <p className="services__accent">
              {mainTranslations.accent}
            </p>
          </AnimatedOnScroll>

          <AnimatedOnScroll animation="fade-up" delay={100}>
            <h4 className="services__title">
              {mainTranslations.mainTitle}
            </h4>
          </AnimatedOnScroll>

          <AnimatedOnScroll animation="fade-up" delay={200}>
            <h5 className="services__subtitle">
              {mainTranslations.subtitle}
            </h5>
          </AnimatedOnScroll>

          <AnimatedOnScroll animation="fade-up" delay={300}>
            <LocalizedNavLink to={mainTranslations.buttonLink} className="services__button">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              {mainTranslations.buttonText}
            </LocalizedNavLink>
          </AnimatedOnScroll>
        </div>
      </div>
      <div className="right__side__services__section">
        {localizedServices.map((service, index) => {
          const titleArray = Array.isArray(service.localizedTitle)
            ? service.localizedTitle
            : [service.localizedTitle];

          return (
            <AnimatedOnScroll
              key={index}
              animation="fade-up"
              delay={index * 100}
              duration={600}
              className="services__links__item"
            >
              <LocalizedNavLink to={service.link} className="link__item">
                {titleArray.map((line, lineIndex) => (
                  <h3 key={lineIndex} className="service__title__line">
                    {line}
                  </h3>
                ))}
              </LocalizedNavLink>
              <p className="services__link__text">
                {service.localizedDescription}
              </p>
            </AnimatedOnScroll>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(ServicesSection);